import {Dispatch, KeyboardEvent, memo, useRef} from 'react';

import styled from 'styled-components';

import {bodyBold, headingXS, subheadingSBold} from '~/shared/theme/typography';
import {getLocalizationService} from '~/shared/services/localisationService';
import {AuthFlowStep, ViewModes} from '~/shared/components/SignIn/consts';
import {TabOrangeLine} from '~/shared/components/TabOrangeLine';
import PieButton from '~/shared/theme/pie/PieButton';
import {flipOnLTR, media} from '~/shared/theme/utils';
import CloseIcon from '~/assets/images/icons/pie/functionalityCloseSmall.svg?react';
import ArrowIcon from '~/assets/images/icons/arrow_left_carousel.svg?react';

const LEFT_ARROW_CODE = 37;
const LEFT_ARROW_KEY = 'ArrowLeft';
const RIGHT_ARROW_CODE = 39;
const RIGHT_ARROW_KEY = 'ArrowRight';

const Root = styled.div<{isTabMode?: boolean}>`
  position: relative;
  width: 100%;
  height: 72px;
  padding: ${({isTabMode}) => (isTabMode ? '28px 72px 0' : '22px 0')};
  display: flex;
  justify-content: center;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  background-color: ${({theme}) => theme.colors.surface};
  border-radius: 16px 16px 0 0;
  text-align: center;
`;

const StyledArrowRightIcon = styled(ArrowIcon).attrs(({$isMinLargeMobile}: {$isMinLargeMobile?: boolean}) => ({
  viewBox: '0 0 16 16',
  width: $isMinLargeMobile ? 20 : 16,
  height: $isMinLargeMobile ? 20 : 16,
}))<{$isMinLargeMobile?: boolean}>`
  ${({theme}) => !theme.isLTR && 'transform: rotate(180deg);'};
`;

const HeaderText = styled.p`
  ${subheadingSBold}
  ${media.minLargeMobile`
    ${headingXS}
  `}
`;

const Tab = styled.button`
  width: 177px;
  display: flex;
  height: 43px;
  justify-content: center;
  padding: 0;
  text-align: unset;
`;

const TabText = styled.p<{isActive?: boolean}>`
  ${bodyBold}
  color: ${({theme, isActive}) => (isActive ? theme.colors.contentDefault : theme.colors.contentInteractiveSubdued)};
`;

const HeaderSideButton = styled(PieButton).attrs(({isMinLargeMobile}: {isMinLargeMobile?: boolean}) => ({
  variant: isMinLargeMobile ? 'secondaryLarge' : 'secondarySmall',
  iconButton: true,
}))<{isTabMode?: boolean; isMinLargeMobile?: boolean}>`
  position: absolute;
  top: 20px;
  ${({isTabMode}) => (isTabMode ?
    flipOnLTR`
      left: 20px;
    ` : flipOnLTR`
      right: 20px;
    `)
}
  ${media.minLargeMobile`
    top: 16px;
  `}
`;

type LoginOrRegisterHeaderProps = {
  viewMode: ViewModes;
  authFlowStep: AuthFlowStep;
  setAuthFlowStep: Dispatch<AuthFlowStep>;
  onSignInClick?: () => void;
  onSignUpClick?: () => void;
  hideHeader?: boolean;
  onBackButtonClick?: () => void;
  isMinLargeMobile?: boolean;
};

const LoginOrRegisterHeader = ({
  viewMode,
  authFlowStep,
  setAuthFlowStep,
  onSignInClick,
  onSignUpClick,
  hideHeader,
  onBackButtonClick,
  isMinLargeMobile,
}: LoginOrRegisterHeaderProps) => {
  const {t} = getLocalizationService();
  const tabsRef = useRef<(HTMLButtonElement | null)[]>([]);

  const currentIndex = viewMode === ViewModes.SIGN_IN_MODE ?
    0 :
    viewMode === ViewModes.SIGN_UP_MODE ?
      1 :
      null;

  const handleTabPanelKeys = (e: KeyboardEvent<HTMLElement>) => {
    if (!tabsRef.current[0] || !tabsRef.current[1]) {
      return;
    }

    const key = e.key || e.keyCode;

    const isLeftOrRightArrowKey =
      key === LEFT_ARROW_CODE || key === LEFT_ARROW_KEY || key === RIGHT_ARROW_CODE || key === RIGHT_ARROW_KEY;
    if (!isLeftOrRightArrowKey) {
      return;
    }

    const [fadingTabIndex, activeTabIndex] = document.activeElement !== tabsRef.current[0] ? [1, 0] : [0, 1];
    const fadingTab = tabsRef.current[fadingTabIndex];
    const activeTab = tabsRef.current[activeTabIndex];

    if (fadingTab && activeTab) {
      fadingTab.tabIndex = -1;
      activeTab.tabIndex = 0;
      activeTab.focus();
    }
  };

  if (hideHeader) {
    return null;
  }

  if (authFlowStep !== AuthFlowStep.INITIAL && authFlowStep !== AuthFlowStep.PASSWORD) {
    return (
      <Root>
        <HeaderText id="modal-title">
          {authFlowStep === AuthFlowStep.ADD_ADDRESS ? t('where_are_we_sending_the_food') : t('code_n_go')}
        </HeaderText>
        {authFlowStep !== AuthFlowStep.ADD_ADDRESS && (
          <HeaderSideButton
            onClick={() => {
              setAuthFlowStep(AuthFlowStep.INITIAL);
            }}
            isMinLargeMobile={isMinLargeMobile}
          >
            <StyledArrowRightIcon $isMinLargeMobile={isMinLargeMobile} />
          </HeaderSideButton>
        )}
      </Root>
    );
  }

  return (
    <Root isTabMode aria-label={t('login_or_register')} onKeyDown={handleTabPanelKeys}>
      <Tab
        ref={el => {
          tabsRef.current[1] = el;
        }}
        onClick={onSignUpClick}
        id="register_tab_button"
        aria-controls="register_tab_controls"
      >
        <TabText isActive={viewMode === ViewModes.SIGN_UP_MODE}>
          {t('sign_up')}
        </TabText>
      </Tab>
      <Tab
        ref={el => {
          tabsRef.current[0] = el;
        }}
        onClick={onSignInClick}
        id="login_tab_button"
        aria-controls="login_tab_controls"
      >
        <TabText isActive={viewMode === ViewModes.SIGN_IN_MODE}>
          {t('log_in')}
        </TabText>
      </Tab>
      {typeof currentIndex === 'number' && <TabOrangeLine elsRefs={tabsRef} currentIndex={currentIndex} />}
      <HeaderSideButton onClick={onBackButtonClick} isTabMode isMinLargeMobile={isMinLargeMobile}>
        <CloseIcon />
      </HeaderSideButton>
    </Root>
  );
};

export default memo(LoginOrRegisterHeader);
